// types/prescription.ts - Clean and consistent type definitions

import {DateUtil} from "@/utils/dateUtil.ts";

export interface Prescription {
  // Core fields - always required
  id: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  
  // Optional main fields
  diagnosis?: string;
  quick_notes?: string;
  advice?: string;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
  last_modified?: string;
  synced_at?: string | null;

  // Populated from joins
  patient_name?: string;
  doctor_name?: string;
  clinic_name?: string;

  // Related data - standardized structure
  vitals?: PrescriptionVitals;
  complaints?: PrescriptionComplaint[];
  medicines?: PrescriptionMedicine[];
  investigations?: string[];
  next_visit?: PrescriptionNextVisit;
}

export interface PrescriptionVitals {
  bp?: string;
  pulse?: string;
  spo2?: string;
  weight?: string;
  temp?: string;
  edema?: string;
  pallor?: string;
  icterus?: string;
}

export interface PrescriptionComplaint {
  id?: string;
  complaint: string;
  frequency?: string;
  severity?: string;
  duration?: string;
  date?: string;
}

export interface PrescriptionMedicine {
  id?: string;
  drug_id?: string | null;
  brand_name?: string;
  generic_name?: string;
  product?: string;
  dosage?: string;
  taken_when?: string;
  duration?: string;
  quantity?: string | number;
  notes?: string;
}

export interface PrescriptionNextVisit {
  date?: string;
  time?: string;
  notes?: string;
}

// Form data interface - exactly matches form structure
export interface PrescriptionFormData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  diagnosis: string;
  quick_notes: string;
  advice: string;
  vitals: PrescriptionVitals;
  complaints: PrescriptionComplaint[];
  medicines: PrescriptionMedicine[];
  investigations: string[];
  next_visit: PrescriptionNextVisit;
}

export interface Patient {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  created_at?: string;
  updated_at?: string;
  last_modified?: string;
}

export interface PrescriptionFormProps {
  mode: "create" | "edit";
  patient_id: string;
  prescription?: Prescription;
  onSaved?: () => void;
  onCancel?: () => void;
}

// Data transformation utilities
export class PrescriptionDataTransformer {
  static toFormData(prescription: Prescription, patient_id: string): PrescriptionFormData {
    return {
      id: prescription.id,
      patient_id: patient_id,
      doctor_id: prescription.doctor_id || "",
      clinic_id: prescription.clinic_id || "",
      // Use the actual prescription date, not current date
      date: prescription.date || new Date().toISOString(),
      diagnosis: prescription.diagnosis || "",
      quick_notes: prescription.quick_notes || "",
      advice: prescription.advice || "",
      vitals: prescription.vitals
          ? {
            bp: prescription.vitals.bp ?? "",
            pulse: prescription.vitals.pulse ?? "",
            spo2: prescription.vitals.spo2 ?? "",
            weight: prescription.vitals.weight ?? "",
            temp: prescription.vitals.temp ?? "",
            edema: prescription.vitals.edema ?? "",
            pallor: prescription.vitals.pallor ?? "",
            icterus: prescription.vitals.icterus ?? ""
          }
          : {
          bp: "", pulse: "", spo2: "", weight: "",
          temp: "", edema: "", pallor: "", icterus: ""
        },
      complaints: prescription.complaints?.length 
        ? prescription.complaints.map(c => ({
            ...c,
            // Normalize null values to empty strings for form compatibility
            complaint: c.complaint ?? "",
            frequency: c.frequency ?? "",
            severity: c.severity ?? "",
            duration: c.duration ?? "",
            date: DateUtil.getDateOnlyFormat(c.date) ?? ""
          }))
        : [{
            complaint: "", frequency: "", severity: "", duration: "", 
            date: new Date().toISOString().split('T')[0]
          }],
      medicines: prescription.medicines?.length 
        ? prescription.medicines.map(m => ({
            // Map product to brand_name for form compatibility
            product: m.product || "",
            brand_name: m.brand_name || m.product || "",
            generic_name: m.generic_name ?? "",
            drug_id: m.drug_id ?? null,
            notes: m.notes ?? "",
            dosage: m.dosage ?? "",
            taken_when: m.taken_when ?? "",
            duration: m.duration ?? "",
            quantity: m.quantity ?? ""
          }))
        : [{
            drug_id: "", product: "", brand_name: "", generic_name: "", dosage: "", taken_when: "", duration: "", quantity: "", notes: ""
          }],
      investigations: prescription.investigations || [],
      next_visit: prescription.next_visit 
        ? {
            date: prescription.next_visit.date ?? "",
            time: prescription.next_visit.time ?? "",
            notes: prescription.next_visit.notes ?? ""
          }
        : { date: "", time: "", notes: "" }
    };
  }

  // Convert form data to API payload
  static toApiPayload(formData: PrescriptionFormData, isUpdate: boolean = false): Prescription {
    // Convert date to YYYY-MM-DD format (remove time if present)
    const formatDate = (dateStr: string): string => {
      if (!dateStr) return "";
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // If it's an ISO string, extract the date part
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      // If it's a datetime string like "2026-01-31 18:47:47", extract date part
      if (dateStr.includes(' ')) {
        return dateStr.split(' ')[0];
      }
      return dateStr;
    };

    const payload: any = {
      patient_id: formData.patient_id,
      doctor_id: formData.doctor_id,
      clinic_id: formData.clinic_id,
      date: formatDate(formData.date),
      diagnosis: formData.diagnosis?.trim() || "",
      quick_notes: formData.quick_notes?.trim() || "",
      advice: formData.advice?.trim() || "",
      vitals: formData.vitals,
      complaints: formData.complaints.filter(c => c.complaint?.trim()),
      medicines: formData.medicines.filter(m => (m.brand_name || m.product)?.trim()),
      investigations: formData.investigations.filter(i => i?.trim()),
      next_visit: formData.next_visit
    };

    // Include id for API calls (used in URL for updates, in body for creates)
    if (formData.id) {
      payload.id = formData.id;
    }

    return payload;
  }

  static createEmpty(patient_id: string, doctor_id: string, clinic_id: string): PrescriptionFormData {
    return {
      patient_id,
      doctor_id: doctor_id || "",
      clinic_id: clinic_id || "",
      date: new Date().toISOString(),
      diagnosis: "",
      quick_notes: "",
      advice: "",
      vitals: {
        bp: "", pulse: "", spo2: "", weight: "", 
        temp: "", edema: "", pallor: "", icterus: ""
      },
      complaints: [{ 
        complaint: "", frequency: "", severity: "", duration: "", 
        date: new Date().toISOString().split('T')[0] 
      }],
      medicines: [{ 
        product: "", dosage: "", taken_when: "", duration: "", quantity: "", notes: "" 
      }],
      investigations: [],
      next_visit: { date: "", time: "", notes: "" }
    };
  }
}

export interface UserSettings {
  profile: {
    name: string;
    email: string;
    phone: string;
    specialty: string;
    license: string;
    signature: string;
  };
  clinic: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  preferences: {
    theme: 'light' | 'dark' | 'system';
    language: string;
    notifications: boolean;
    autoSave: boolean;
    printFormat: 'a4' | 'letter' | 'compact';
  };
}